import type { SeriesClickCallbackFunction } from 'highcharts';
import { useMemo } from 'react';

interface LineChartOptions {
  subtitle?: string;
  onClick?: SeriesClickCallbackFunction;
  /**
   * Formats the aggregated point value for the (export-only) series data
   * labels (e.g. the 2-dp consequence cost formatter). The line tooltip is
   * built inline in GigaLineWidget (custom date-axis formatting) using
   * {@link formatLineTooltipValue}. When omitted, options are unchanged
   * (RSP-5735).
   */
  valueFormatter?: (value: number) => string;
}

/**
 * Resolves the display value for a line tooltip point. Exported so
 * GigaLineWidget's inline tooltip formatter stays thin and the
 * cost-formatting branch is unit-testable without faking a Highcharts
 * tooltip context (RSP-5735).
 */
export const formatLineTooltipValue = (
  y: number | null | undefined,
  valueFormatter?: (value: number) => string
): number | null | string | undefined =>
  valueFormatter ? valueFormatter(y ?? 0) : y;

export const useGetLineChartDefaultOptions = ({
  subtitle,
  onClick,
  valueFormatter,
}: LineChartOptions): Highcharts.Options => {
  return useMemo(() => {
    const options: Highcharts.Options = {
      chart: {
        type: 'line',
      },
      subtitle: {
        text: subtitle || '',
      },
      yAxis: {
        min: 0,
        allowDecimals: false,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        series: {
          events: {
            click: onClick,
          },
          ...(valueFormatter
            ? {
                dataLabels: {
                  formatter: function lineDataLabelFormatter(
                    this: Highcharts.Point
                  ) {
                    return valueFormatter(this.y ?? 0);
                  },
                },
              }
            : {}),
        },
      },
      tooltip: {
        headerFormat: '<b>{category}</b><br/>',
        pointFormat: '{series.name}: {point.y}',
      },
    };

    return options;
  }, [subtitle, onClick, valueFormatter]);
};
